import { action, computed, makeObservable, observable, toJS } from "mobx";
import { DbExtensionSettings } from "./DatabaseTypes";
import { requireEvidenceDatabase } from "./getDatabase";
import { DataGoesWhereSettingsPartial, DataGoesWhereSettingsUpdate, defaultDataGoesWhereSettings } from "../../lib/MultiWorld/DataGoesWhereSettings";
import { lazyObservable } from "mobx-utils";
import { getUserChosenDirectoryHandle, verifyFileSystemPermission } from "../FileSystem";
import { ExtensionSettingsUpdated } from "../StateEvents";
import { AnnouncementExtensionSettingsUpdatedMessageName } from "../../constants";

export class DbExtensionSettingsTable {

	static readLatest = async () => {
		const db = await requireEvidenceDatabase();
		const tx = db.transaction("extensionSettings", "readonly");
		try {
			// Get reverse cursor to pull out the last element
			const cursor = await tx.store.openCursor(null, 'prev');
			tx.commit();
			return cursor?.value ?? { settings: {} };
		} catch (e) {
			tx.abort();
			throw e;
		}
	}

	static write = async (dbExtensionSettings: DbExtensionSettings, writeNew: boolean = false) => {
		const db = await requireEvidenceDatabase();
		let {settingsIndex, ...dbExtensionSettingsWithoutSettingsIndex} = dbExtensionSettings;
		if (typeof settingsIndex === "number" && !writeNew) {
			 settingsIndex = await db.put("extensionSettings", dbExtensionSettings);
		} else {
			 settingsIndex = await db.add("extensionSettings", dbExtensionSettingsWithoutSettingsIndex);
		}
		
		ExtensionSettingsUpdated.send({});
		return settingsIndex;
	}
}

export class ExtensionSettings {
	settingsIndex?: number;
	protected _settingsSpecified: DataGoesWhereSettingsPartial;
	protected updateSettingsSpecified = action ((settings: DataGoesWhereSettingsPartial) => {
		this._settingsSpecified = settings;
	});

	@computed get settingsSpecified() {
		return {...this._settingsSpecified};
	}
	@computed get settings() {
		return {...defaultDataGoesWhereSettings, ...this._settingsSpecified};
	}

	@computed get sharedSearchTermField() {
		return this.settings.sharedSearchTerms ?? "";
	}

	@computed get sharedSearchTermArray() {
		return this.sharedSearchTermField.split("\n").map( s => s.trim() ).filter( s => s.length > 0);
	}

	protected constructor(initialSettings: DbExtensionSettings) {
		this.settingsIndex = initialSettings.settingsIndex;
		this._settingsSpecified = initialSettings.settings;
		makeObservable<this, "_settingsSpecified">(this, {
			"_settingsSpecified": observable
		});
		ExtensionSettingsUpdated.addListener( async () => {
			const {settingsIndex, settings} = await DbExtensionSettingsTable.readLatest();;
			this.settingsIndex = settingsIndex;
			this.updateSettingsSpecified(settings);
		});
	}

	static #create = async () => {
		return new ExtensionSettings(await DbExtensionSettingsTable.readLatest());
	}
	static #instancePromise: Promise<ExtensionSettings> | undefined;
	static get() {
		return ExtensionSettings.#instancePromise ?? (ExtensionSettings.#instancePromise = ExtensionSettings.#create());
	}

	@computed static get lazy() {
		return lazyObservable<ExtensionSettings>( async (sink) =>
			sink(await ExtensionSettings.get()))
	}
	@computed static get current() {
		return ExtensionSettings.lazy.current()
	}

	getArchiveDirectoryWithPermissions = async () => {
		const directory = this.settings.archiveDirectoryHandle;
		if (directory != null && await verifyFileSystemPermission(directory)) {
			return directory;
		}
		return undefined;
	}

	setSettings = async (settings: DbExtensionSettings) => {
	}

	updateSettingObservable = action (<KEY extends keyof DataGoesWhereSettingsUpdate>(key: KEY, value: DataGoesWhereSettingsUpdate[KEY]) => {
    if (value == null) {
      delete this._settingsSpecified[key];
    } else {
      this._settingsSpecified[key] = value;
    }
  })

	#write = async () => {
		const {settingsIndex, settingsSpecified} = this;
		this.settingsIndex = await DbExtensionSettingsTable.write({settingsIndex, settings: toJS(settingsSpecified)});
  }

	updateSetting = async <KEY extends keyof DataGoesWhereSettingsUpdate>(key: KEY, value: DataGoesWhereSettingsUpdate[KEY]) => {
    this.updateSettingObservable(key, value);
    await this.#write();
  }

  updateSettings = async (newSettings?: DataGoesWhereSettingsUpdate) => {
    if (newSettings == null) return;
    for (const [key, value] of Object.entries(newSettings) as [keyof DataGoesWhereSettingsUpdate, DataGoesWhereSettingsUpdate[keyof DataGoesWhereSettingsUpdate]][]) {
      this.updateSettingObservable(key, value);
    }
    await this.#write();
  };

}

export const extensionSettingsIfLoaded = () => {
	lazyObservable<DbExtensionSettingsTable>( async (sink) => sink(await ExtensionSettings.get()) ).current();
};

export const getDirectoryToSaveInto = async () => {
	const settingsController = await ExtensionSettings.get();
	const directoryHandle = await getUserChosenDirectoryHandle({
		startIn: settingsController.settings.archiveDirectoryHandle,
		mode: 'readwrite'
	});
	return directoryHandle;
}